import { media } from "../../design/breakpoints";
import axios from "axios";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { GlobalState } from "../../GlobalState.js";
import { editAccess } from "../utils/utils.js";
import { formatOfflineDuration } from "../utils/formatOfflineDuration";
import styled, { keyframes } from "styled-components";

/* Identical to PumpStatusV2/V3's PbBlock */
const PbBlock = styled.div`
  position: relative;
  flex: 0 0 auto;
  width: fit-content;
  min-width: 180px;
  max-width: 340px;
  margin: 0.5rem;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  display: flex;
  transition: all 0.3s ease;

  ${media.max480} {
    flex: 1 1 100%;
    max-width: 100%;
    min-width: 0;
    margin: 0.3rem 0;
  }
`;

const AccentBar = styled.div`
  width: 5px;
  flex-shrink: 0;
  background: ${({ $isOnline }) => ($isOnline ? "#22C55E" : "#94A3B8")};
`;

const PbInner = styled.div`
  flex: 1;
  padding: 4px 4px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

/* Identical to PumpStatusV2's ToggleWrapper — plain filter tint, no clamp */

const PbMeta = styled.div`
  flex: 1;
  min-width: 0;
`;

const LocationName = styled.p`
  margin: 0;
  font-weight: 700;
  font-size: 0.85rem;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  color: #111827;

  ${media.max768} {
    font-size: 0.75rem;
  }
`;


const Badge = styled.span`
  font-size: 9.5px;
  font-weight: 500;
  padding: 2px 7px;
  border-radius: 8px;
  white-space: nowrap;
  flex-shrink: 0;
  background: ${({ $isOnline }) =>
    $isOnline ? "rgba(34,197,94,.12)" : "#F3F4F6"};
  color: ${({ $isOnline }) => ($isOnline ? "#15803D" : "#090a0e")};
`;

const BadgeStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  flex-shrink: 0;
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.08);
  }
`;

const PlainDuration = styled.span`
  display: inline-block; /* Important */
  font-size: 10px;
  font-weight: 500;
  color: #d81111;
  white-space: nowrap;
  animation: ${pulse} 2s infinite ease-in-out;
  transform-origin: center;
`;



/* Robust boolean coercion — handles booleans, "1"/"0", "true"/"false", numbers.
   Boolean("false") is a real trap since any non-empty string is truthy in JS. */
function toBool(value) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    return v === "1" || v === "true" || v === "on";
  }
  return Boolean(value);
}

function useTouchTooltip(ref) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let hideTimer;

    const onTouchStart = (e) => {
      e.preventDefault();
      el.classList.add("touch-active");
      clearTimeout(hideTimer);
      hideTimer = setTimeout(() => el.classList.remove("touch-active"), 2500);
    };

    el.addEventListener("touchstart", onTouchStart, { passive: false });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      clearTimeout(hideTimer);
    };
  }, [ref]);
}

/* ── Valve icon button (Option B) ─────────────────────────────────────────── */
const ValveIconBtn = styled.button`
  width: 40px; height: 40px; border-radius: 10px;
  border: 2px solid ${({ $open, $online }) =>
    !$online ? "#E5E7EB" : $open ? "#237A35" : "#D1D5DB"};
  background: ${({ $open, $online }) =>
    !$online ? "#F9FAFB" : $open ? "#F0FDF4" : "#F9FAFB"};
  display: flex; align-items: center; justify-content: center;
  cursor: ${({ $online }) => $online ? "pointer" : "default"};
  flex-shrink: 0;
  transition: all .15s;
  &:hover {
    ${({ $online }) => $online && `border-color: #237A35; background: #F0FDF4;`}
  }
`;

export default function SolenoidBoxComponent({ sensor }) {
  const history = useHistory();
  const state = useContext(GlobalState);

  const tooltipRef = useRef(null);
  useTouchTooltip(tooltipRef);

  const [solenoid, setSolenoid] = useState({
    ...sensor,
    sensor_online: toBool(sensor.sensor_online),
  });
  const [solenoidStatus, setSolenoidStatus] = useState(toBool(sensor.valve_on));

  const MAX_LENGTH = 15;
  const truncatedLocationName =
    solenoid.location_name?.length > MAX_LENGTH
      ? solenoid.location_name.slice(0, MAX_LENGTH)
      : solenoid.location_name;

  const fetchData = async (id) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API}/sensor/solenoid?solenoid_id=${id}`,
        { headers: { Authorization: state.token[0] } }
      );

      const data = response.data?.data;
      if (data) {
        setSolenoid({
          solenoid_id: data.solenoid_id,
          sensor_online: toBool(data.sensor_online),
          valve_on: data.valve_on,
          updatedAt: data.updatedAt,
          location_name: data.location_name || sensor.location_name,
        });
        setSolenoidStatus(toBool(data.valve_on));
      }
    } catch (err) {
      console.error("Error fetching solenoid data:", err);
    }
  };

  const toggleSolenoid = async (id) => {
    const previousStatus = solenoidStatus;
    try {
      setSolenoidStatus((prev) => !prev);

      const response = await axios.patch(
        `${process.env.REACT_APP_API}/sensor/solenoid?solenoid_id=${id}`,
        { valve_on: !previousStatus ? "1" : "0" },
        { headers: { Authorization: state.token[0] } }
      );

      if (response.data) {
        await fetchData(id);
      }
    } catch (err) {
      console.error("Error toggling solenoid:", err);
      setSolenoidStatus(previousStatus);
    }
  };

  useEffect(() => {
    if (sensor.solenoid_id) {
      fetchData(sensor.solenoid_id);
      const interval = setInterval(() => fetchData(sensor.solenoid_id), 5000);
      return () => clearInterval(interval);
    }
  }, [sensor.solenoid_id]);

  const isOnline = solenoid.sensor_online;

  const statusLabel = !isOnline
    ? "OFFLINE"
    : solenoidStatus
    ? "OPEN · running"
    : "CLOSED";

  return (
    <PbBlock>
      <AccentBar $isOnline={isOnline} />

      <PbInner>
        <TopRow>
          <ValveIconBtn
            $open={solenoidStatus}
            $online={isOnline}
            disabled={!editAccess(state.userAPI) || !isOnline}
            onClick={(e) => { e.stopPropagation(); toggleSolenoid(solenoid.solenoid_id); }}
          >
            {/* Valve SVG — green when open, grey when closed, red X overlay when closed */}
            <svg width="22" height="22" viewBox="0 0 30 30" fill="none"
              stroke={!isOnline ? "#D1D5DB" : solenoidStatus ? "#237A35" : "#9CA3AF"}
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="2" y1="15" x2="9" y2="15"/>
              <line x1="21" y1="15" x2="28" y2="15"/>
              <polygon points="9,10 9,20 15,15"/>
              <polygon points="21,10 21,20 15,15"/>
              <line x1="15" y1="10" x2="15" y2="6"/>
              <rect x="10" y="2" width="10" height="4" rx="1"/>
              {!solenoidStatus && isOnline && (
                <>
                  <line x1="12" y1="13" x2="18" y2="17" stroke="#EF4444" strokeWidth="2.5"/>
                  <line x1="18" y1="13" x2="12" y2="17" stroke="#EF4444" strokeWidth="2.5"/>
                </>
              )}
            </svg>
          </ValveIconBtn>

          <PbMeta>
            <LocationName
              onClick={() =>
                history.push(`/solenoid/${solenoid.solenoid_id}/${solenoid.location_name}`)
              }
            >
              {truncatedLocationName}
            </LocationName>
            <div style={{
              fontSize: "11px", fontWeight: 600, marginTop: "2px",
              color: !isOnline ? "#9CA3AF" : solenoidStatus ? "#237A35" : "#6B7280"
            }}>
              {statusLabel}
            </div>
          </PbMeta>

          <BadgeStack>
            <Badge $isOnline={isOnline}>{isOnline ? "online" : "offline"}</Badge>

            {!isOnline && (
              <PlainDuration>
                {formatOfflineDuration(solenoid.updatedAt)}
              </PlainDuration>
            )}
          </BadgeStack>
        </TopRow>
      </PbInner>
    </PbBlock>
  );
}